/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'

import {
  FormControl,
  InputAdornment,
  IconButton,
  Icon as ClearIcon,
  Box,
  Typography,
  Grid,
  Chip,
  Container
} from '@mui/material'

import { CustomOutlinedInput } from '../styles/mui_custom_components'
import MainTable from '../components/fieldForms/MainTable'
import { useTranslation } from 'react-i18next'
import { fieldFormStyles } from '../styles/classes/FieldFormClasses'

import { useHistory } from 'react-router-dom'

// api
import { getScreenConfigs } from '../services/ApiService'
// utils
import { statusOptions } from '../utils/fieldForms'
// redux
import { useDispatch, useSelector } from 'react-redux'
import { dataActions } from '../store/data'
import NewFormModal from '../components/fieldForms/NewFormModal'
import * as ApiServices from '../services/ApiService'
import { sortBy } from '../utils/global'
import { store } from '../store'
import moment from 'moment'
import { auditOptions, auditType } from '../utils/formBuilder'
import { loadingActions } from '../store/loading'
import { faCircleXmark } from '@fortawesome/pro-light-svg-icons'
import { set } from 'react-hook-form'

function FieldForms () {
  const dispatch = useDispatch()
  const screenConfigs = useSelector(state => state.data.screenConfigsList)
  const currentUser = useSelector(state => state.auth.user)
  const classes = fieldFormStyles().classes
  const history = useHistory()

  const { t } = useTranslation()

  const [query, setQuery] = useState('')
  const [showAddFormModal, setShowAddFormModal] = useState(false)
  const [fieldFormsList, setFieldFormsList] = useState(null)
  const [loading, setLoading] = useState(false)

  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(50)
  const [search, setSearch] = useState('')
  const [isFetching, setIsFetching] = useState(true)
  const [hasMore, setHasMore] = useState(true)

  const debounce = useRef(null)

  useEffect(() => {
    dataActions.setScreenConfigsList([])
  })

  useEffect(() => {
    if (loading) {
      updateTradesAndTypes()
      setTimeout(() => setLoading(false), 500)
    }
  }, [fieldFormsList])

  useEffect(() => {
    loadData()
  }, [page, search])

  const loadData = async () => {
    store.dispatch(loadingActions.show())
    const configId = currentUser?.userInfo?.config_id
    const screenConfigsUpdated = await getScreenConfigs(configId, page, limit, search)
    setHasMore(screenConfigsUpdated?.length > 0)

    if (page === 1) {
      setFieldFormsList(screenConfigsUpdated)
    } else {
      setFieldFormsList(prev => {
        const newList = [...prev, ...screenConfigsUpdated]
        return newList
      })
    }

    // get config id from user
    dispatch(dataActions.setScreenConfigsList(screenConfigsUpdated))
    if (screenConfigs.length > 0) {
      setLoading(true)
    }
    store.dispatch(loadingActions.hide())
    setTimeout(() => {
      setIsFetching(false)
    }, 2000)
  }

  const handleScrollBottom = () => {
    if (!isFetching && hasMore) {
      setIsFetching(true)
      setPage(prev => {
        const newPage = prev + 1
        return newPage
      })
    }
  }

  const updateTradesAndTypes = async () => {
    store.dispatch(loadingActions.show())
    // get trades and save trades
    const trades = await ApiServices.getTrades()
    const sortedTrades = trades.trades.sort((a, b) => sortBy(a, b, 'name'))
    store.dispatch(dataActions.setTrades(sortedTrades))

    // wo types and save types
    const wotypes = await ApiServices.getWorkOrderTypes()
    const sortedTypes = wotypes.work_order_types.sort((a, b) =>
      sortBy(a, b, 'name')
    )
    store.dispatch(dataActions.setWorkOrderTypes(sortedTypes))
    store.dispatch(loadingActions.hide())
  }

  const openAddFormModalHandler = () => {
    setShowAddFormModal(true)
  }

  const handleChangeSearch = event => {
    const value = event?.target?.value ?? ''
    const oldQuery = query
    setQuery(value)

    if (debounce.current) {
      clearTimeout(debounce.current)
    }
    debounce.current = setTimeout(async () => {
      if (value.length < 3 && oldQuery.length < 3) {
        return
      }
      setPage(1)
      setSearch(value)
      dataActions.setScreenConfigsList([])
    }, 1000)
  }

  const getLastModifiedObject = () => {
    const unixTime = moment().unix()
    return {
      user_id: currentUser?.userId,
      date: unixTime,
      firstName: currentUser?.userInfo?.firstName,
      lastName: currentUser?.userInfo?.lastName
    }
  }

  const editHandler = screenConfig => {
    // add the new Screen configuration to store
    dispatch(dataActions.setCurrentScreenConfig(screenConfig))
    history.push('/form-builder')
  }

  const createNewAudit = async (auditOption, screenConfigId) => {
    const unixTime = moment().unix()
    const auditToCreate = {
      user_id: currentUser.userId,
      date: unixTime,
      type: auditType,
      change: {
        status: auditOption
      },
      first_name: currentUser?.userInfo?.firstName,
      last_name: currentUser?.userInfo?.lastName,
      record_id: screenConfigId
    }
    await ApiServices.createAudit(auditToCreate)
  }

  const cloningHandler = async screenConfig => {
    const objectToCreate = { ...screenConfig, status: statusOptions.DRAFT }
    delete objectToCreate.id
    let newScreenConfiguration = null
    newScreenConfiguration = await ApiServices.createScreenConfiguration(
      objectToCreate
    )
    if (newScreenConfiguration) {
      // create audit cloned for new screen config
      await createNewAudit(auditOptions.CLONED, newScreenConfiguration.id)
      // add the new Screen configuration to the store list
      dispatch(
        dataActions.setScreenConfigsList([
          newScreenConfiguration,
          ...screenConfigs
        ])
      )
      setFieldFormsList([newScreenConfiguration, ...screenConfigs])
    }

    if (newScreenConfiguration) {
      editHandler(newScreenConfiguration)
    }
  }

  const removeHandler = async screenConfig => {
    // send minial requirement for update
    const objectToUpdate = {
      form_name: screenConfig.formName,
      form_description: screenConfig.formDescription,
      status: statusOptions.DELETED,
      last_modified: getLastModifiedObject(),
      config_id: currentUser?.userInfo?.config_id
    }
    try {
      const updated = await ApiServices.updateScreenConfiguration(
        screenConfig.id,
        objectToUpdate
      )
      if (updated) {
        const newList = screenConfigs.filter(
          item => item.id !== screenConfig.id
        )
        dispatch(dataActions.setScreenConfigsList(newList))
        setFieldFormsList(newList)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const actionsHandler = (action, data) => {
    switch (action) {
      case 'edit':
        editHandler(data)
        break
      case 'clone':
        cloningHandler(data)
        break
      case 'remove':
        removeHandler(data)
        break
      default:
        break
    }
  }

  return (
    <Container
      disableGutters
      maxWidth
      className={classes.container}
    >
      <Grid container spacing={0}>
        <Grid item xs>
          <Box className={classes.actionsContainer}>
            <Typography className={classes.fieldFormsText}>
              {t('field_forms.field_forms')}
            </Typography>
            <Chip
              className={classes.newFormButton}
              label={`+ ${t('field_forms.new_form')}`}
              variant="outlined"
              onClick={openAddFormModalHandler}
            />
            <FormControl>
              <CustomOutlinedInput
                value={query}
                className={classes.SearchInput}
                id="component-outlined"
                placeholder={t('general.labels.search')}
                onInput={handleChangeSearch}
                onChange={handleChangeSearch}
                startAdornment={
                  <InputAdornment position="start">
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      size="xs"
                      color="grey"
                    />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    {query.length > 0 && (
                      <IconButton
                        onClick={() => {
                          setQuery('')
                          handleChangeSearch()
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faCircleXmark}
                          size="xs"
                          color="grey"
                          style={{ fontSize: '14px' }}
                        />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
                autoComplete="off"
              />
            </FormControl>
          </Box>
        </Grid>
        {/* TODO help unhide and functionality */}
        <Grid hidden item sx={{ width: 200 }}>
          <Chip
            sx={{ fontSize: 12, marginRight: '5px' }}
            label="Help"
            variant="outlined"
          />
        </Grid>
      </Grid>

      <MainTable
        items={fieldFormsList}
        onAction={actionsHandler}
        loading={loading}
        onScrollBottom={handleScrollBottom}
      />
      <NewFormModal
        open={showAddFormModal}
        onClose={() => setShowAddFormModal(false)}
      />
    </Container>
  )
}

export default FieldForms
